import { Dispatch, ReactElement, SetStateAction } from "react"
import Select, { SingleValue } from "react-select"
import { Button, FormLabel } from "@wfp/ui"
import { SelectOption, SeriesPageFilters } from "interfaces/dataInterfaces"
import { useGetPriceTypeQuery } from "services/core/priceType"
import { useGetCurrenciesQuery } from "services/core/currencies"
import { useGetCommoditiesUnitQuery, useGetCommodityByCountryQuery } from "services/core/commodities"
import { useGetMarketsQuery } from "services/core/markets"
import PermissionWrapper from "components/Shared/Common/PermissionWrapper"
import { MARKET_MODULE } from "constants/constants"

interface Props {
  onApply: () => void
  onClear: () => void
  filters: SeriesPageFilters
  setFilters: Dispatch<SetStateAction<SeriesPageFilters>>
  setFilterTags: Dispatch<SetStateAction<{ key: string; label: string; value: string }[]>>
}

const permissionsProps = {
  moduleId: MARKET_MODULE,
  subModule: "ccpucs",
}

const AdvancedFilters = ({ onApply, onClear, filters, setFilters, setFilterTags }: Props): ReactElement => {
  const { data: priceTypeList } = useGetPriceTypeQuery()
  const { data: currencies } = useGetCurrenciesQuery({})
  const { data: units } = useGetCommoditiesUnitQuery({ skip: 0, take: 0 })
  const { data: markets } = useGetMarketsQuery(
    { adm0code: filters.adm0Code, skip: 0, top: 0 },
    { skip: !filters.adm0Code, refetchOnMountOrArgChange: true },
  )
  const { data: commodities } = useGetCommodityByCountryQuery(
    {
      admCode: filters.adm0Code,
    },
    { skip: !filters.adm0Code },
  )
  const visibilityOptions: SelectOption[] = [
    { value: true, label: "Public" },
    { value: false, label: "Internal use" },
  ]

  const buildAppliedTags = (): { key: string; label: string; value: string }[] => {
    const tags: { key: string; label: string; value: string }[] = []

    if (filters.market && filters.market !== 0) {
      const m = markets?.items?.find(x => x.marketID === filters.market)
      if (m) tags.push({ key: "market", label: "Market:", value: m.marketName })
    }
    if (filters.commodity && filters.commodity !== 0) {
      const c = commodities?.find(x => x.commodityId === filters.commodity)
      if (c) tags.push({ key: "commodity", label: "Commodity:", value: c.commodityName })
    }
    if (filters.unit && filters.unit !== 0) {
      const u = units?.items?.find(x => x.commodityUnitID === filters.unit)
      if (u) tags.push({ key: "unit", label: "Unit:", value: u.commodityUnitName })
    }
    if (filters.currency && filters.currency !== 0) {
      const ccy = currencies?.items?.find(x => x.currencyID === filters.currency)
      if (ccy) tags.push({ key: "currency", label: "Currency:", value: ccy.currencyName })
    }
    if (filters.priceType && filters.priceType !== 0) {
      const pt = priceTypeList?.find(x => x.priceTypeID === filters.priceType)
      if (pt) tags.push({ key: "priceType", label: "Price Type:", value: pt.priceTypeName })
    }
    if (filters.seriesCombinationVisibility !== null) {
      const vis = visibilityOptions.find(v => v.value === filters.seriesCombinationVisibility)
      if (vis)
        tags.push({
          key: "seriesCombinationVisibility",
          label: "Visibility:",
          value: vis.value ? "Visible" : "Hidden",
        })
    }

    return tags
  }

  return (
    <div className="p-5 bg-slate-200 mt-2 rounded-lg">
      <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 w-full">
        <div>
          <FormLabel htmlFor="category">Market</FormLabel>
          <Select
            isSearchable
            classNamePrefix="select"
            placeholder="Select market"
            isClearable
            isDisabled={!filters.adm0Code}
            options={markets?.items ?? []}
            getOptionLabel={opt => opt.marketName}
            getOptionValue={opt => opt.marketID.toString()}
            value={markets?.items.find(m => m.marketID === filters.market) ?? null}
            onChange={e => {
              setFilters({ ...filters, market: e?.marketID ?? 0 })
            }}
          />
        </div>
        <div>
          <FormLabel htmlFor="category">Commodity</FormLabel>
          <Select
            key={filters.adm0Code}
            isSearchable
            isDisabled={!filters.adm0Code}
            classNamePrefix="select"
            placeholder="Select commodity"
            isClearable
            options={commodities ?? []}
            getOptionLabel={opt => opt.commodityName}
            getOptionValue={opt => opt.commodityId.toString()}
            onChange={e => {
              setFilters({ ...filters, commodity: e?.commodityId ?? 0 })
            }}
          />
        </div>
        <div>
          <FormLabel htmlFor="category">Unit</FormLabel>
          <Select
            isSearchable
            isClearable
            classNamePrefix="select"
            placeholder="Select unit"
            options={units?.items ?? []}
            getOptionLabel={opt => opt.commodityUnitName}
            getOptionValue={opt => opt.commodityUnitID.toString()}
            isDisabled={!filters.adm0Code}
            value={units?.items.find(u => u.commodityUnitID === filters.unit) ?? null}
            onChange={e => {
              setFilters({ ...filters, unit: e?.commodityUnitID ?? 0 })
            }}
          />
        </div>
        <div>
          <FormLabel htmlFor="category">Currency</FormLabel>
          <Select
            isSearchable
            isClearable
            classNamePrefix="select"
            placeholder="Select currency"
            options={currencies?.items ?? []}
            getOptionLabel={opt => opt.currencyName}
            getOptionValue={opt => opt.currencyID.toString()}
            isDisabled={!filters.adm0Code}
            value={currencies?.items.find(c => c.currencyID === filters.currency) ?? null}
            onChange={e => {
              setFilters({ ...filters, currency: e?.currencyID ?? 0 })
            }}
          />
        </div>
        <div>
          <FormLabel htmlFor="category">Price Type</FormLabel>
          <Select
            isSearchable
            isClearable
            classNamePrefix="select"
            placeholder="Select price type"
            options={priceTypeList ?? []}
            getOptionLabel={opt => opt.priceTypeName}
            getOptionValue={opt => opt.priceTypeID.toString()}
            isDisabled={!filters.adm0Code}
            value={priceTypeList?.find(p => p.priceTypeID === filters.priceType) ?? null}
            onChange={e => {
              setFilters({ ...filters, priceType: e?.priceTypeID ?? 0 })
            }}
          />
        </div>
        <PermissionWrapper {...permissionsProps} feature="edit">
          <div>
            <FormLabel htmlFor="category">Visibility</FormLabel>
            <Select
              isSearchable
              isClearable
              classNamePrefix="select"
              placeholder="Select Visiblity"
              options={visibilityOptions}
              isDisabled={!filters.adm0Code}
              value={visibilityOptions.find(o => o.value === filters.seriesCombinationVisibility) ?? null}
              onChange={(opt: SingleValue<SelectOption>) => {
                setFilters({ ...filters, seriesCombinationVisibility: (opt?.value as boolean) ?? null })
              }}
            />
          </div>
        </PermissionWrapper>
      </div>
      <div className="flex justify-end mt-2">
        <Button
          className="mr-2"
          onClick={() => {
            onClear()
            setFilterTags([])
          }}
          kind="ghost"
        >
          Clear filters
        </Button>
        <Button
          onClick={() => {
            setFilterTags(buildAppliedTags())
            onApply()
          }}
        >
          Apply
        </Button>
      </div>
    </div>
  )
}

export default AdvancedFilters
